import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Product } from '@commercetools/frontend-domain-types/product/Product';
import { Transition } from '@headlessui/react';
import { MagnifyingGlassIcon as SearchIcon, XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
// @ts-ignore
import debounce from 'lodash.debounce';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import useScrollBlock from '@loomstateorg/shared-frontend/hooks/useScrollBlock';
import { Category } from '@loomstateorg/shared-frontend/types/category';
import { useProduct } from 'frontastic';
import Overlay from '@loomstateorg/shared-frontend/components/commercetools-ui/atoms/overlay';
import SearchItem from './search-item';

interface Props {
  categories: Category[];
  autoFocus?: boolean;
  submitButtonClassName?: string;
  searchIconFocusedClassName?: string;
  searchIconClassName?: string;
}

const Search: React.FC<Props> = ({
  categories,
  autoFocus,
  submitButtonClassName,
  searchIconFocusedClassName = 'fill-white',
  searchIconClassName = 'fill-primary-regular',
}) => {
  const form = useRef<HTMLFormElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const { query: queryProducts } = useProduct();

  const [items, setItems] = useState<Product[]>([]);

  const [focused, setFocused] = useState(false);

  const onFocus = useCallback(() => setFocused(true), []);
  const onBlur = useCallback(() => setFocused(false), []);

  const { formatMessage } = useFormat({ name: 'common' });

  const { blockScroll } = useScrollBlock();

  const router = useRouter();

  const [value, setValue] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (autoFocus) {
      input.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    blockScroll(focused);
  }, [blockScroll, focused]);

  useEffect(() => {
    const handleRouteChange = () => {
      setFocused(false);
    };

    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  const fetchResults = useCallback(async () => {
    const results = await queryProducts({ query, limit: 6 });

    if (results.isError) return;

    setItems((results.data.items as Product[]) ?? []);
  }, [query, queryProducts]);

  //eslint-disable-next-line react-hooks/exhaustive-deps
  const updateQuery = useCallback(
    debounce((value: string) => setQuery(value), 150),
    [],
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      updateQuery(e.target.value);
    },
    [updateQuery],
  );

  const cleanUp = useCallback(() => {
    updateQuery('');
    setValue('');
  }, [updateQuery]);

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!value) return;

      setFocused(false);
      input.current?.blur();
      router.push(`/search?q=${encodeURIComponent(value)}`);
    },
    [value, router],
  );

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  return (
    <>
      {focused && <Overlay showSpinner={false} />}
      <div
        className={`relative lg:p-20 lg:min-w-[min(92vw,56rem)] xl:min-w-0${focused ? ' z-[200]' : ''}`}
        data-testid="atom-search"
      >
        <div className={`relative w-full rounded-lg border-b border-neutral-400 bg-site${focused ? ' z-50' : ''}`}>
          <form
            className="quick-search relative flex w-full items-stretch"
            ref={form}
            onSubmit={onSubmit}
            data-testid="atom-search-form"
          >
            <input
              ref={input}
              data-testid="atom-search-input"
              className="box-content w-[27vw] min-w-0 grow border-none !bg-site p-0 px-12 py-10 transition placeholder:text-14 placeholder:text-primary-regular focus:outline-none lg:w-full xl:w-[27vw]"
              value={value}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              placeholder={`${formatMessage({ id: 'search.placeholder', defaultMessage: 'Type to search' })}...`}
            />
            <button
              type="submit"
              data-testid="atom-search-submit"
              title={formatMessage({ id: 'search', defaultMessage: 'Search' })}
              className={`shrink-0 px-16 py-10 transition ${focused ? 'bg-secondary-regular' : 'bg-site'} ${submitButtonClassName ?? ''}`}
            >
              <SearchIcon className={`size-24 stroke-0 ${focused ? searchIconFocusedClassName : searchIconClassName}`} />
            </button>
            {value && (
              <button
                type="reset"
                data-testid="atom-search-clear"
                aria-label={formatMessage({ id: 'clear', defaultMessage: 'Clear' })}
                className="absolute right-70 top-1/2 block -translate-y-1/2"
                onClick={cleanUp}
                onMouseDown={(e) => e.preventDefault()}
              >
                <CloseIcon className="w-24 fill-primary-regular" />
              </button>
            )}
          </form>
        </div>

        {focused && (
          <Transition
            appear
            show
            className="absolute bottom-0 left-0 max-h-[60vh] w-full translate-y-full overflow-auto bg-site px-20 py-28 lg:max-h-[unset] lg:translate-y-[calc(100%-85px)] lg:rounded-md lg:pt-84"
            enter="transition duration-75"
            enterFrom="opacity-0"
            enterTo="opacity-100"
          >
            <div
              className="grid grid-cols-1 gap-30 md:grid-cols-2"
              onMouseDown={(e) => e.preventDefault()}
              data-testid="atom-search-results"
            >
              {items.slice(0, 6).map((item, index) => (
                <SearchItem key={item.variants?.[0].sku} hit={item} categories={categories} index={index} />
              ))}
            </div>
          </Transition>
        )}
      </div>
    </>
  );
};

export default Search;
